/**
 * Become Teacher Form Component
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ImageUpload, IMAGE_FORMATS } from '../ui/ImageUpload';
import { GoogleRatingInput } from '../GoogleRatingInput';
import { ChevronRight, Info } from 'lucide-react';
import axios from 'axios';
import { AVAILABLE_COUNTRIES } from '../../data/postalCodes';
import { API_URL, SOCIAL_PLATFORMS, initialSocialLinks, isSpainCountry, lookupPostalCodeWithGeo } from './formUtils';

export function BecomeTeacherForm() {
  const { t, i18n } = useTranslation();
  const { getAuthHeaders } = useAuthStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  const [profileImage, setProfileImage] = useState(null);
  const [bioEn, setBioEn] = useState('');
  const [bioEs, setBioEs] = useState('');
  const [bioIt, setBioIt] = useState('');
  const [specialties, setSpecialties] = useState('');
  const [experience, setExperience] = useState('');
  const [certifications, setCertifications] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('España');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [area, setArea] = useState('');
  const [cityAutoFilled, setCityAutoFilled] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Google Business Rating
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [googleRating, setGoogleRating] = useState(null);
  const [googleReviewsCount, setGoogleReviewsCount] = useState(null);
  
  // Social links state
  const [socialLinks, setSocialLinks] = useState(initialSocialLinks);
  
  const updateSocialLink = (platform, value) => {
    setSocialLinks(prev => ({ ...prev, [platform]: value }));
  };

  // Auto-fill city, province, area when postal code changes
  useEffect(() => {
    const lookupAddress = async () => {
      if (postalCode.length >= 4) {
        try {
          const result = await lookupPostalCodeWithGeo(postalCode, country);
          if (result.found) {
            if (result.hasMultipleCities) {
              // Multiple cities - let user select
              setCityOptions(result.cities || []);
              setCity('');
              setCityAutoFilled(false);
            } else {
              setCity(result.city || '');
              setProvince(result.province || '');
              setArea(result.area || '');
              setCityAutoFilled(true);
              setCityOptions([]);
            }
          }
        } catch (error) {
          console.log('Postal code lookup error:', error);
        }
      }
    };
    
    lookupAddress();
  }, [postalCode, country]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bioEs.trim() || !city.trim() || !country.trim() || !phone.trim() || !postalCode.trim()) {
      alert(t('teacher.becomeTeacher.requiredFieldsError'));
      return;
    }
    
    if (!acceptedTerms) {
      alert(t('teacher.becomeTeacher.termsRequiredError'));
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/teachers/register`,
        {
          bio: {
            en: bioEn || bioEs,
            es: bioEs,
            it: bioIt || bioEs,
          },
          specialties: specialties.split(',').map(s => s.trim()).filter(Boolean),
          experience_years: parseInt(experience) || 0,
          certifications: certifications.split(',').map(s => s.trim()).filter(Boolean),
          images: profileImage ? [profileImage] : [],
          location: {
            address: '',
            city,
            province,
            area,
            country,
            postal_code: postalCode,
            latitude: 0,
            longitude: 0,
          },
          phone,
          website: website || null,
          social_links: {
            instagram: socialLinks.instagram || null,
            facebook: socialLinks.facebook || null,
            youtube: socialLinks.youtube || null,
            tiktok: socialLinks.tiktok || null,
            linkedin: socialLinks.linkedin || null,
            twitter: socialLinks.twitter || null,
            telegram: socialLinks.telegram || null,
          },
          // Google Business Rating
          google_maps_url: googleMapsUrl || null,
          google_rating: googleRating,
          google_reviews_count: googleReviewsCount,
        },
        { headers: getAuthHeaders() }
      );
      
      alert(t('teacher.becomeTeacher.successMessage'));
      navigate('/profile');
    } catch (error) {
      alert(error.response?.data?.detail || t('teacher.becomeTeacher.errorSubmit'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in" data-testid="become-teacher-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {t('common.back')}
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">{t('profile.becomeTeacher')}</h1>
      
      {/* Info Card */}
      <div className="flex gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#F5EFE6' }}>
        <Info className="w-6 h-6 text-gold flex-shrink-0" style={{ color: '#D4A574' }} />
        <p className="text-sm text-secondary">
          {t('teacher.becomeTeacher.infoText')}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* Profile Image */}
        <ImageUpload
          image={profileImage}
          onImageChange={setProfileImage}
          label={t('teacher.becomeTeacher.profilePhoto')}
          aspectRatio={IMAGE_FORMATS.PROFILE.aspectRatio}
          maxSize={IMAGE_FORMATS.PROFILE.maxSize}
          shape="circle"
          placeholder={t('teacher.becomeTeacher.addPhoto')}
          folder="teachers/"
          language={i18n.language}
        />
        
        {/* Bio Fields */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('teacher.becomeTeacher.bioSpanish')} *</label>
          <Textarea
            placeholder={t('teacher.becomeTeacher.bioSpanishPlaceholder')}
            value={bioEs}
            onChange={(e) => setBioEs(e.target.value)}
            rows={4}
            required
            data-testid="bio-es"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('teacher.becomeTeacher.bioEnglish')}</label>
          <Textarea
            placeholder={t('teacher.becomeTeacher.bioEnglishPlaceholder')}
            value={bioEn}
            onChange={(e) => setBioEn(e.target.value)}
            rows={4}
            data-testid="bio-en"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('teacher.becomeTeacher.bioItalian')}</label>
          <Textarea
            placeholder={t('teacher.becomeTeacher.bioItalianPlaceholder')}
            value={bioIt}
            onChange={(e) => setBioIt(e.target.value)}
            rows={4}
            data-testid="bio-it"
          />
        </div>
        
        {/* Specialties & Experience */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('teacher.becomeTeacher.specialtiesLabel')}</label>
          <Input
            placeholder={t('teacher.becomeTeacher.specialtiesPlaceholder')}
            value={specialties}
            onChange={(e) => setSpecialties(e.target.value)}
            data-testid="specialties"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('teacher.becomeTeacher.certificationsLabel')}</label>
          <Input
            placeholder={t('teacher.becomeTeacher.certificationsPlaceholder')}
            value={certifications}
            onChange={(e) => setCertifications(e.target.value)}
            data-testid="certifications"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('teacher.becomeTeacher.yearsExperience')}</label>
          <Input
            type="number"
            placeholder="5"
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
            min={0}
            data-testid="experience"
          />
        </div>
        
        {/* Contact Information */}
        <h3 className="font-semibold text-primary pt-4 border-t">{t('teacher.becomeTeacher.contactInfo')}</h3>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('teacher.becomeTeacher.phoneLabel')} *</label>
          <Input
            placeholder={t('teacher.becomeTeacher.phonePlaceholder')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            data-testid="phone"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('teacher.becomeTeacher.websiteLabel')}</label>
          <Input
            placeholder={t('teacher.becomeTeacher.websitePlaceholder')}
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            type="url"
            data-testid="website"
          />
        </div>
        
        {/* Social Links */}
        <h3 className="font-semibold text-primary pt-4 border-t">{t('teacher.becomeTeacher.socialLinks')}</h3>
        <p className="text-sm text-secondary mb-4">{t('teacher.becomeTeacher.socialLinksDescription')}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SOCIAL_PLATFORMS.map(platform => (
            <div key={platform.key}>
              <label className="block text-sm font-medium text-secondary mb-1">{platform.label}</label>
              <Input
                placeholder={platform.placeholder}
                value={socialLinks[platform.key]}
                onChange={(e) => updateSocialLink(platform.key, e.target.value)}
                type="url"
              />
            </div>
          ))}
        </div>
        
        {/* Google Business Rating */}
        <GoogleRatingInput
          googleMapsUrl={googleMapsUrl}
          setGoogleMapsUrl={setGoogleMapsUrl}
          googleRating={googleRating}
          setGoogleRating={setGoogleRating}
          googleReviewsCount={googleReviewsCount}
          setGoogleReviewsCount={setGoogleReviewsCount}
          showVerificationStatus={false}
        />
        
        {/* Location */}
        <h3 className="font-semibold text-primary pt-4 border-t">{t('teacher.becomeTeacher.locationTitle')}</h3>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('teacher.becomeTeacher.countryLabel')} *</label>
          <Input
            placeholder="España"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            required
            data-testid="country"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{t('teacher.becomeTeacher.postalCodeLabel')} *</label>
            <Input
              placeholder="Ej: 28001"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              maxLength={10}
              required
              data-testid="postal-code"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">
              {t('teacher.becomeTeacher.cityLabel')} * 
              {cityAutoFilled && <span className="text-green-600 text-xs ml-1">{t('teacher.becomeTeacher.autoFilled')}</span>}
            </label>
            {cityOptions.length > 0 ? (
              <select
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  setCityAutoFilled(true);
                  setCityOptions([]);
                }}
                required
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gold bg-white"
                data-testid="city-select"
              >
                <option value="">{t('common.selectCity', 'Selecciona una ciudad')}</option>
                {cityOptions.map((opt, idx) => (
                  <option key={idx} value={opt.city}>{opt.city}</option>
                ))}
              </select>
            ) : (
              <Input
                placeholder={t('teacher.becomeTeacher.cityAutoPlaceholder') || 'Compilato automaticamente dal CAP'}
                value={city}
                readOnly
                required
                className="bg-gray-100 cursor-not-allowed border-gray-200"
                data-testid="city"
              />
            )}
          </div>
        </div>
        
        {/* Terms and Conditions Checkbox */}
        <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50">
          <input
            type="checkbox"
            id="accept-terms-teacher"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-gold focus:ring-gold"
            style={{ accentColor: '#D4A574' }}
            data-testid="accept-terms-checkbox"
          />
          <label htmlFor="accept-terms-teacher" className="text-sm text-secondary cursor-pointer">
            {t('teacher.becomeTeacher.termsAccept')}{' '}
            <Link to="/terms" target="_blank" className="text-gold hover:underline font-medium" style={{ color: '#D4A574' }}>
              {t('teacher.becomeTeacher.termsLink')}
            </Link>
            {' '}{t('teacher.becomeTeacher.andThe')}{' '}
            <Link to="/professional-guidelines" target="_blank" className="text-gold hover:underline font-medium" style={{ color: '#D4A574' }}>
              {t('teacher.becomeTeacher.guidelinesLink')}
            </Link>.
          </label>
        </div>
        
        <Button 
          type="submit" 
          disabled={isLoading} 
          className="w-full bg-gold hover:bg-gold/90 text-white py-3" 
          style={{ backgroundColor: '#D4A574' }}
          data-testid="submit-teacher"
        >
          {isLoading ? t('teacher.becomeTeacher.submitting') : t('teacher.becomeTeacher.submitButton')}
        </Button>
      </form>
    </div>
  );
}
